import moment from "moment";
import { useEffect, useState } from "react";
import { Card, Col, Row, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import API from "../../common/API";
import Settings from "../../common/Settings";

const RecordCentro = (props: any) => {
    const { id } = props;
    const [records, setRecords] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const { t } = useTranslation();

    const bindRecords = async () => {
        if (!id) return;

        try {
            setIsLoading(true);
            // Normalize cedula (remove hyphens)
            const normalizedId = id.replace("-", "");
            const response = await API.getAction(
                `centros/Consulta?cedula=${normalizedId}&page=${page}&limit=${limit}`
            );

            if (response.status === 200 && response.data.status === "ok") {
                const data = response.data.response || [];
                setRecords(data);
                setTotalPages(response.data.quantityOfPages || 0);
                setTotalRecords(response.data.quantityOfRecords || 0);
                setPage(page);
            } else {
                setRecords([]);
                setTotalPages(0);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching detention records:", error);
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        bindRecords();
    }, [id, limit]);

    if (records.length === 0 && !isLoading) {
        return null;
    }

    const getStatusBadge = (condicion: string) => {
        const statusMap: { [key: string]: string } = {
            "Procesado": "danger",
            "Condenado": "danger",
            "Preventivo": "warning",
            "Libertad": "success",
            "default": "secondary"
        };

        let bgColor = statusMap["default"];
        Object.keys(statusMap).forEach(key => {
            if (condicion && condicion.includes(key)) {
                bgColor = statusMap[key];
            }
        });

        return bgColor;
    };

    const renderDetentionTable = () => (
        <div className="table-responsive">
            <table className="table table-striped table-hover">
                <thead className="table-dark">
                    <tr>
                        <th style={{ width: "80px" }}>
                            <i className="fas fa-image"></i>
                        </th>
                        <th>{t("Centro")}</th>
                        <th>{t("Fecha Ingreso")}</th>
                        <th>{t("Condición Jurídica")}</th>
                        <th>{t("Violación")}</th>
                        <th>{t("Condena")}</th>
                        <th>{t("Tribunal")}</th>
                    </tr>
                </thead>
                <tbody>
                    {records.map((record, index) => (
                        <tr key={index}>
                            <td>
                                {record.picBase64 ? (
                                    <img
                                        src={record.picBase64}
                                        alt={`${record.nombres} ${record.apellidos}`}
                                        className="img-thumbnail"
                                        style={{
                                            width: "60px",
                                            height: "75px",
                                            objectFit: "cover"
                                        }}
                                    />
                                ) : record.pic ? (
                                    <a href={`${Settings.BasePath}${record.pic.includes("/") ? "" : "files/"}${record.pic}`}
                                        target="_blank" rel="noopener noreferrer">
                                        <img
                                            src={`${Settings.BasePath}${record.pic.includes("/") ? "" : "files/"}${record.pic}`}
                                            alt={`${record.nombres}`}
                                            className="img-thumbnail"
                                            style={{
                                                width: "60px",
                                                height: "75px",
                                                objectFit: "cover"
                                            }}
                                        />
                                    </a>
                                ) : (
                                    <div
                                        className="bg-light border rounded"
                                        style={{
                                            width: "60px",
                                            height: "75px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <small className="text-muted">{t("N/A")}</small>
                                    </div>
                                )}
                            </td>
                            <td className="fw-bold">
                                <div>{record.centroId}</div>
                                <small className="text-muted">{t("Imputaciones")}: {record.ficha}</small>
                            </td>
                            <td>
                                {record.fechaIngreso ? (
                                    <>
                                        <div>{moment(record.fechaIngreso).format("DD/MM/YYYY")}</div>
                                        <small className="text-muted">
                                            {moment(record.fechaIngreso).fromNow()}
                                        </small>
                                    </>
                                ) : (
                                    <span className="text-muted">{t("N/A")}</span>
                                )}
                            </td>
                            <td>
                                <Badge bg={getStatusBadge(record.condicionJuridica)}>
                                    {record.condicionJuridica || t("Desconocida")}
                                </Badge>
                            </td>
                            <td className="text-wrap">
                                <small>{record.violacion || "-"}</small>
                            </td>
                            <td className="text-wrap">
                                <small>{record.condena || "-"}</small>
                            </td>
                            <td className="text-wrap">
                                <small>{record.tribunal || "-"}</small>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderDetailCards = () => (
        <Row className="g-3 mb-4">
            {records.map((record, index) => (
                <Col md={6} lg={4} key={index}>
                    <Card className="h-100 shadow-sm border-start border-4 border-danger">
                        <Card.Body>
                            {record.picBase64 || record.pic ? (
                                <div className="mb-3">
                                    <img
                                        src={record.picBase64 || `${Settings.BasePath}${record.pic.includes("/") ? "" : "files/"}${record.pic}`}
                                        alt={record.nombres}
                                        className="img-fluid rounded"
                                        style={{ maxHeight: "150px", objectFit: "cover" }}
                                    />
                                </div>
                            ) : null}

                            <h6 className="fw-bold text-danger mb-2">
                                {record.nombres} {record.apellidos}
                            </h6>

                            <div className="mb-2">
                                <small className="text-muted d-block">{t("Cédula")}</small>
                                <code className="text-dark">{record.cedula}</code>
                            </div>

                            <div className="mb-2">
                                <small className="text-muted d-block">{t("Centro")}</small>
                                <strong>{record.centroId}</strong>
                            </div>

                            <div className="mb-2">
                                <small className="text-muted d-block">{t("Fecha Ingreso")}</small>
                                {record.fechaIngreso ? (
                                    <>
                                        <div className="fw-bold">
                                            {moment(record.fechaIngreso).format("DD/MM/YYYY")}
                                        </div>
                                        <small className="text-info">
                                            {moment(record.fechaIngreso).fromNow()}
                                        </small>
                                    </>
                                ) : (
                                    <span className="text-muted">-</span>
                                )}
                            </div>

                            <div className="mb-2">
                                <small className="text-muted d-block">{t("Condición Jurídica")}</small>
                                <Badge bg={getStatusBadge(record.condicionJuridica)}>
                                    {record.condicionJuridica || t("Desconocida")}
                                </Badge>
                            </div>

                            {record.violacion && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">{t("Violación")}</small>
                                    <small className="text-dark">{record.violacion}</small>
                                </div>
                            )}

                            {record.condena && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">{t("Condena")}</small>
                                    <small className="text-dark">{record.condena}</small>
                                </div>
                            )}

                            {record.tribunal && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">{t("Tribunal")}</small>
                                    <small className="text-dark">{record.tribunal}</small>
                                </div>
                            )}

                            {record.edad && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">{t("Edad")}</small>
                                    <strong>{record.edad} {t("años")}</strong>
                                </div>
                            )}

                            {record.nacionalidad && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">{t("Nacionalidad")}</small>
                                    <small>{record.nacionalidad}</small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            ))}
        </Row>
    );

    return (
        <Card className="mb-4 shadow-sm border-left-danger">
            <Card.Header className="bg-danger text-white">
                <Card.Title className="mb-0">
                    <i className="fas fa-building"></i> {t("Centros de Detención")}
                    <Badge bg="light" text="dark" className="ms-2">
                        {totalRecords} {totalRecords === 1 ? t("registro") : t("registros")}
                    </Badge>
                </Card.Title>
            </Card.Header>

            {isLoading ? (
                <Card.Body className="text-center py-5">
                    <div className="spinner-border text-danger" role="status">
                        <span className="visually-hidden">{t("Cargando...")}</span>
                    </div>
                </Card.Body>
            ) : records.length > 0 ? (
                <>
                    <Card.Body>
                        {/* Table view for screens >= md */}
                        <div className="d-none d-md-block">
                            {renderDetentionTable()}
                        </div>

                        {/* Card view for mobile */}
                        <div className="d-md-none">
                            {renderDetailCards()}
                        </div>
                    </Card.Body>


                </>
            ) : (
                <Card.Body className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                    <p>{t("No se encontraron registros de detención")}</p>
                </Card.Body>
            )}
        </Card>
    );
};

export default RecordCentro;
